import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, Container, Button, Card, CardContent, CircularProgress, useTheme } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_BASE_URL } from '../config';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [token, setToken] = useState(null);
  const [toolName, setToolName] = useState("");
  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    document.title = "Payment Successful | CoreTechTalks";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Your payment was successful. Download your purchased developer utility tools from CoreTechTalks.");
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    fetch(`${API_BASE_URL}/api/verify-session?session_id=${sessionId}`)
      .then(res => {
        if (!res.ok) throw new Error("Verification failed");
        return res.json();
      })
      .then(data => {
        setToken(data.token);
        setToolName(data.tool_name);
        setStatus('success');
      })
      .catch(err => {
        console.error("Verification error:", err);
        setStatus('error');
      });
  }, [sessionId]);

  // Determine glow orb style based on status
  const getOrbColor = () => {
    if (status === 'success') return 'rgba(34, 197, 94, 0.1)';
    if (status === 'error') return 'rgba(239, 68, 68, 0.08)';
    return 'rgba(99, 102, 241, 0.08)';
  };

  return (
    <Box sx={{ flexGrow: 1, py: { xs: 8, md: 12 }, minHeight: 'calc(100vh - 200px)', position: 'relative', display: 'flex', alignItems: 'center' }}>
      {/* Background decorations */}
      <Box className="glow-orb" sx={{ width: '400px', height: '400px', top: '-100px', right: '-100px', background: getOrbColor() }} />
      <Box className="glow-orb" sx={{ width: '300px', height: '300px', bottom: '10%', left: '-150px', background: 'rgba(6, 182, 212, 0.06)' }} />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Card 
          sx={{ 
            p: { xs: 2, sm: 4 }, 
            textAlign: 'center',
            '&:hover': { transform: 'none' } // static status card, don't hover float
          }}
        >
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: { xs: 3, sm: 4 } }}>
            {status === 'verifying' && (
              <>
                <CircularProgress size={64} color="primary" sx={{ mb: 4 }} />
                <Typography variant="h2" component="h1" sx={{ mb: 2, fontSize: { xs: '28px', md: '36px' } }}>
                  Verifying <span className="gradient-text">Payment</span>
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: '380px', mx: 'auto', mb: 2 }}>
                  We are securely confirming your transaction with Stripe. This will only take a moment...
                </Typography>
              </>
            )}

            {status === 'error' && (
              <>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: mode === 'light' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.15)',
                    color: 'error.main',
                    mb: 3,
                    boxShadow: '0 0 20px rgba(239, 68, 68, 0.1)'
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: '48px' }} />
                </Box>
                <Typography variant="h2" component="h1" color="error.main" sx={{ mb: 2, fontSize: { xs: '28px', md: '36px' }, fontWeight: 800 }}>
                  Verification Failed
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 4, maxWidth: '400px', mx: 'auto' }}>
                  We could not verify your payment session. If you believe this is an error or your card was charged, please contact our support.
                </Typography>
                <Button 
                  id="btn-success-error-back-to-tools"
                  component={Link} 
                  to="/tools" 
                  variant="contained" 
                  color="primary"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  sx={{ px: 4, py: 1.5, borderRadius: '12px' }}
                >
                  Return to Tools
                </Button>
              </>
            )}

            {status === 'success' && (
              <>
                <Box 
                  sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: mode === 'light' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.15)',
                    color: '#22C55E',
                    mb: 3,
                    boxShadow: '0 0 20px rgba(34, 197, 94, 0.15)',
                    animation: 'float 6s ease-in-out infinite'
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: '48px' }} />
                </Box>
                
                <Typography variant="h2" component="h1" sx={{ mb: 2, fontSize: { xs: '32px', md: '40px' } }}>
                  Payment <span style={{ color: '#22C55E', fontFamily: '"Outfit", sans-serif', fontWeight: 800 }}>Successful!</span>
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, px: { xs: 0, sm: 2 } }}>
                  Thank you for purchasing <strong>{toolName || 'your utility tool'}</strong>. Your secure download link has been generated.
                </Typography>

                {/* Info Card inside Success */}
                <Box 
                  sx={{ 
                    width: '100%', 
                    p: 2.5, 
                    borderRadius: '12px', 
                    bgcolor: mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
                    border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid rgba(255, 255, 255, 0.05)',
                    textAlign: 'left',
                    mb: 4
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                    Important Download Details:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    <li>This secure link expires in <strong>10 minutes</strong>.</li>
                    <li>The package can be downloaded only once via this link.</li>
                    <li>An email containing the receipt and backup download link has been sent.</li>
                  </ul>
                </Box>
                
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button 
                    id="btn-download-tool"
                    variant="contained" 
                    color="primary" 
                    size="large"
                    startIcon={<DownloadIcon />}
                    href={`${API_BASE_URL}/api/download?token=${token}`}
                    sx={{ 
                      py: 1.8, 
                      fontSize: '16px',
                      borderRadius: '12px',
                      fontWeight: 700
                    }}
                  >
                    Download File Now
                  </Button>

                  <Button 
                    id="btn-success-back-to-tools"
                    component={Link}
                    to="/tools"
                    variant="outlined" 
                    color="primary" 
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                      py: 1.5, 
                      fontSize: '15px',
                      borderRadius: '12px'
                    }}
                  >
                    Back to Utility Tools
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PaymentSuccess;
